import React, { useState } from 'react';
import { X, Lock, User as UserIcon, LogIn, UserPlus, AlertCircle, Sparkles } from 'lucide-react';
import { ANIME_AVATARS } from '../data/initialCatalog';
import { registerWithUsername, loginWithUsername } from '../lib/firebase';
import { UserProfile } from '../types';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: UserProfile) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(ANIME_AVATARS[0].url);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!username.trim()) {
      setError("Si us plau, introdueix un nom d'usuari.");
      return;
    }

    if (password.length < 6) {
      setError("La contrasenya ha de tenir com a mínim 6 caràcters.");
      return;
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        const user = await registerWithUsername(
          username.trim(),
          password,
          displayName.trim() || username.trim(),
          selectedAvatar
        );
        onSuccess(user);
        onClose();
      } else {
        const user = await loginWithUsername(username.trim(), password);
        onSuccess(user);
        onClose();
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      if (err.code === 'auth/email-already-in-use' || err.code === 'auth/credential-already-in-use') {
        setError("Aquest nom d'usuari ja està registrat. Prova d'iniciar sessió.");
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        setError("Nom d'usuari o contrasenya incorrectes.");
      } else {
        setError(err.message || "S'ha produït un error durant l'autenticació.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      id="auth-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-md bg-[#0e121c] border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-black text-left"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          id="close-auth-button"
          onClick={onClose}
          className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="text-center space-y-2 mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-red-500 text-black shadow-lg shadow-amber-500/20 mb-1">
            <Sparkles className="w-6 h-6" />
          </div>
          <h2 className="font-display font-black text-2xl text-white">
            {mode === 'login' ? 'Benvingut a AnimeCat' : 'Crea el teu compte'}
          </h2>
          <p className="text-xs text-slate-400">
            {mode === 'login' 
              ? 'Inicia sessió per guardar la teva llista i historial de visualització.'
              : 'Registra\'t per accedir a tot el catàleg d\'anime doblat en català.'}
          </p>
        </div>

        {/* Mode Switcher Tabs */}
        <div className="flex bg-white/5 p-1 rounded-xl mb-6 border border-white/5">
          <button
            id="tab-login"
            onClick={() => {
              setMode('login');
              setError(null);
            }}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
              mode === 'login' 
                ? 'bg-amber-500 text-black shadow-md' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Inicia Sessió
          </button>
          <button
            id="tab-signup"
            onClick={() => {
              setMode('signup');
              setError(null);
            }}
            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${
              mode === 'signup' 
                ? 'bg-amber-500 text-black shadow-md' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Registra't
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {mode === 'signup' && (
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                Nom Visible (Opcional)
              </label>
              <input
                id="input-displayname"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ex: Son Goku Cat"
                className="w-full px-4 py-2.5 rounded-xl bg-slate-900/90 border border-white/10 text-white text-sm focus:border-amber-500 focus:outline-none placeholder-slate-500"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">
              Nom d'Usuari
            </label>
            <div className="relative">
              <UserIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                id="input-username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ex: goku_90 o conan"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-900/90 border border-white/10 text-white text-sm focus:border-amber-500 focus:outline-none placeholder-slate-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">
              Contrasenya (mínim 6 caràcters)
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                id="input-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-900/90 border border-white/10 text-white text-sm focus:border-amber-500 focus:outline-none placeholder-slate-500"
              />
            </div>
          </div>

          {/* Avatar Selector (If Signup) */}
          {mode === 'signup' && (
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-2">
                Tria el teu Avatar d'Anime
              </label>
              <div className="grid grid-cols-6 gap-2">
                {ANIME_AVATARS.map((avatar) => (
                  <button
                    key={avatar.id}
                    type="button"
                    onClick={() => setSelectedAvatar(avatar.url)}
                    className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all ${
                      selectedAvatar === avatar.url 
                        ? 'border-amber-400 scale-105 shadow-md shadow-amber-500/30' 
                        : 'border-white/10 opacity-70 hover:opacity-100'
                    }`}
                    title={avatar.name}
                  >
                    <img
                      src={avatar.url}
                      alt={avatar.name}
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            id="auth-submit-button"
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold text-sm shadow-lg shadow-amber-500/25 flex items-center justify-center gap-2 transition-all mt-2 disabled:opacity-50"
          >
            {loading ? (
              <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : mode === 'login' ? (
              <>
                <LogIn className="w-4 h-4" />
                <span>Entra a AnimeCat</span>
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                <span>Registra'm</span>
              </>
            )}
          </button>
        </form>

      </div>
    </div>
  );
};
